import {
  AwardLetters,
  ElectionChased,
  ElectionSaved,
  PastDeadlineElections,
  SaveLetterWording,
  SaveSpotCitation,
  SpotLetterPreview,
  type ChaseElection,
  type CloseElectionAsCash,
  type ExtendElection,
  type GenerateLetters,
  type ReleaseLetters,
  type ReviewLetter,
  type SaveLetterFallback,
} from '@myrewards/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { request } from './client';

// The steps after the check: each person's letter (SA3), made from a template, read, then released
// to its holder all together; and an election past its deadline (SA4), chased, extended or closed
// as all cash. Every write answers with the server's new view or receipt.

/** Ids come from the address bar or a server answer: only a UUID ever reaches a request path. */
const Id = z.uuid();

function seg(id: string): string {
  return encodeURIComponent(Id.parse(id));
}

function award(id: string): string {
  return `/api/v1/awards/${seg(id)}`;
}

// --- Letters (SA3) ---------------------------------------------------------------------------------

export const letterKeys = {
  one: (requestId: string) => ['letters', requestId] as const,
  spotPreview: (nominationId: string, personId: string) =>
    ['letters', 'spot-preview', nominationId, personId] as const,
  /** Every colleague's preview on one nomination, to read again once it changes (APP-249). */
  spotPreviews: (nominationId: string) => ['letters', 'spot-preview', nominationId] as const,
};

// --- Spot letter preview (APP-242) ---------------------------------------------------------------

function spotPreview(nominationId: string, personId: string): string {
  return `/api/v1/spot/${seg(nominationId)}/nominees/${seg(personId)}/letter-preview`;
}

/**
 * A Spot colleague's letter as it would be printed now: its title, amount and date, and where each
 * comes from. For the committee, the colleague's manager and the Stock Admin team; a 404 for anyone
 * else, the nominator included.
 */
export function spotLetterPreviewQuery(nominationId: string, personId: string) {
  return queryOptions({
    queryKey: letterKeys.spotPreview(nominationId, personId),
    queryFn: ({ signal }) =>
      request(spotPreview(nominationId, personId), { schema: SpotLetterPreview, signal }),
    staleTime: 10_000,
  });
}

/** The preview PDF itself, marked PREVIEW on every page, to frame with `PdfPreview` or open. */
export const spotLetterPreviewHref = (nominationId: string, personId: string): string =>
  `${spotPreview(nominationId, personId)}/file`;

/** Each person's letter, its holder and what's left before release (`GET …/letters`). */
export function lettersQuery(requestId: string) {
  return queryOptions({
    queryKey: letterKeys.one(requestId),
    queryFn: ({ signal }) =>
      request(`${award(requestId)}/letters`, { schema: AwardLetters, signal }),
    staleTime: 10_000,
  });
}

/**
 * Resolve each person's approved mapping, or apply an explicit template override with a reason.
 * Missing facts stop generation with every issue listed. A new award date applies to all letters.
 */
export function generateLetters(requestId: string, body: GenerateLetters): Promise<AwardLetters> {
  return request(`${award(requestId)}/letters/generate`, {
    method: 'POST',
    body,
    schema: AwardLetters,
  });
}

/** APP-201: the award's letter wording, once for everyone on it; null goes back to the default. */
export function saveLetterWording(
  requestId: string,
  body: SaveLetterWording,
): Promise<AwardLetters> {
  return request(`${award(requestId)}/letter-wording`, {
    method: 'PUT',
    body: SaveLetterWording.parse(body),
    schema: AwardLetters,
  });
}

/** Recipient-safe wording, with the award and line versions checked by the server. */
export function savePublicCitation(
  requestId: string,
  lineId: string,
  body: SaveSpotCitation,
): Promise<AwardLetters> {
  return request(`${award(requestId)}/lines/${seg(lineId)}/citation`, {
    method: 'PUT',
    body: SaveSpotCitation.parse(body),
    schema: AwardLetters,
  });
}

/**
 * Release every letter to its holder together. The award date must be the one the letters were
 * made with; the holders chosen here are sent, everyone else keeps theirs or the default.
 */
export function releaseLetters(requestId: string, body: ReleaseLetters): Promise<AwardLetters> {
  return request(`${award(requestId)}/letters/release`, {
    method: 'POST',
    body,
    schema: AwardLetters,
  });
}

/**
 * One person's letter, read (APP-130): Looks right, Something's wrong with what's wrong, or a
 * Something's wrong cleared with a reason. The letter as read is named, so one made again since
 * must be read again.
 */
export function reviewLetter(
  requestId: string,
  lineId: string,
  body: ReviewLetter,
): Promise<AwardLetters> {
  return request(`${award(requestId)}/letters/${seg(lineId)}/review`, {
    method: 'POST',
    body,
    schema: AwardLetters,
  });
}

/**
 * Another approved letter for one gap (signer × mix) until its own letter is ready, with the
 * reason; `templateId: null` stops using it. It makes no letter: Generate does.
 */
export function saveLetterFallback(
  requestId: string,
  body: SaveLetterFallback,
): Promise<AwardLetters> {
  return request(`${award(requestId)}/letters/fallback`, {
    method: 'POST',
    body,
    schema: AwardLetters,
  });
}

/**
 * One person's letter for SA3's preview frame: inline, framed only by the app (APP-067, as
 * APP-047's employee preview). The server checks who may read it on every request.
 */
export function letterInlineHref(requestId: string, lineId: string): string {
  return `${award(requestId)}/letters/${seg(lineId)}/inline`;
}

/** One person's letter as a download. The server checks who may read it on every request. */
export function letterFileHref(requestId: string, lineId: string): string {
  return `${award(requestId)}/letters/${seg(lineId)}/content`;
}

// --- Elections past their deadline (SA4) ------------------------------------------------------------

function line(requestId: string, lineId: string): string {
  return `${award(requestId)}/lines/${seg(lineId)}/election`;
}

export const pastDeadlineKeys = { all: ['elections', 'past-deadline'] as const };

/**
 * Everyone past their election deadline, one row per person (APP-067): the deadline in force, the
 * days since the letter, every contact so far and what can be done today.
 */
export const pastDeadlineQuery = queryOptions({
  queryKey: pastDeadlineKeys.all,
  queryFn: ({ signal }) =>
    request('/api/v1/elections/past-deadline', { schema: PastDeadlineElections, signal }),
  staleTime: 10_000,
});

/** Email the employee again, now (an election reminder marked as chased). Once a day. */
export function chaseElection(
  requestId: string,
  lineId: string,
  body: ChaseElection,
): Promise<ElectionChased> {
  return request(`${line(requestId, lineId)}/chase`, {
    method: 'POST',
    body,
    schema: ElectionChased,
  });
}

/** A new deadline, with a reason: the election opens again and its reminders run again. */
export function extendElection(
  requestId: string,
  lineId: string,
  body: ExtendElection,
): Promise<ElectionSaved> {
  return request(`${line(requestId, lineId)}/extend`, {
    method: 'POST',
    body,
    schema: ElectionSaved,
  });
}

/** Close the election as all cash (choice (a)), with a reason: the line goes to Payroll. */
export function closeElectionAsCash(
  requestId: string,
  lineId: string,
  body: CloseElectionAsCash,
): Promise<ElectionSaved> {
  return request(`${line(requestId, lineId)}/close-as-cash`, {
    method: 'POST',
    body,
    schema: ElectionSaved,
  });
}
